import React, { useEffect, useState } from 'react';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
} from '@claimup/shared/constants/expedient-documents';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import MainLayout from '../../layouts/MainLayout';
import userStore from '../../store/userStore';
import api, { getErrorMessage } from '../../services/api';
import { expedientStatusLabel } from '@claimup/shared/constants/expedient';

/**
 * El volcado de PDF (docs/volcado-pdf-documentos-plan.md): se suben muchos PDF de
 * golpe, el servidor propone el expediente de cada uno (por el nombre del fichero
 * o por su texto: número de factura o CIF del deudor), la persona corrige y
 * confirma.
 *
 * La empresa con AR lo hace con su cartera; el admin elige antes la empresa.
 */

// Lo que acepta el servidor por envío: los lotes se mandan seguidos.
export const BATCH_SIZE = 10;
const MAX_FILE_MB = 15;

const KEY_LABELS = { invoice: 'nº de factura', tax_id: 'CIF del deudor' };
const SOURCE_LABELS = { file_name: 'en el nombre', content: 'en el contenido' };

export const describeResult = r => {
  switch (r.status) {
    case 'matched':
      return { text: `Encontrado: ${KEY_LABELS[r.key]} ${SOURCE_LABELS[r.source]}`, tone: 'text-emerald-700' };
    case 'ambiguous':
      return { text: 'Varios expedientes posibles: elige uno', tone: 'text-amber-700' };
    case 'duplicate':
      return { text: 'Ya está en el expediente: no se vuelve a subir', tone: 'text-slate-500' };
    case 'error':
      return { text: r.error || 'No se ha podido leer', tone: 'text-red-700' };
    default:
      return {
        text: r.has_text === false ? 'Sin texto (¿escaneado?): elige el expediente' : 'Sin coincidencia: elige el expediente',
        tone: 'text-amber-700',
      };
  }
};

const chunks = (list, size) => {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

const candidateLabel = c =>
  `${c.deudor}${c.referencia ? ` · ${c.referencia}` : ''} · ${c.importe_deuda ?? ''} ${c.divisa || 'EUR'}${c.estado ? ` · ${expedientStatusLabel(c.estado)}` : ''}`;

/** Buscar un expediente a mano, para lo que no se ha reconocido. */
const ExpedientSearch = ({ companyId, onPick }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      api
        .get('/bulk-documents/search', { params: { q: q.trim(), ...(companyId ? { company_id: companyId } : {}) } })
        .then(({ data }) => setResults(data.data))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [q, companyId]);

  return (
    <div className='relative'>
      <input
        type='search'
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder='Buscar deudor, CIF o factura'
        className='w-full rounded-lg border border-slate-300 px-2 py-1 text-sm'
      />
      {results.length > 0 && (
        <ul className='absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white shadow-sm text-sm'>
          {results.map(c => (
            <li key={c.id}>
              <button
                type='button'
                onClick={() => {
                  onPick(c);
                  setQ('');
                }}
                className='w-full text-left px-2 py-1.5 hover:bg-slate-50'
              >
                {candidateLabel(c)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const BulkDocumentsPage = () => {
  const { user } = userStore();
  const isAdmin = user?.role === 'admin';

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  // Una fila por fichero: { file, result, choice (expediente elegido), picked (de la búsqueda) }
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  // La clasificacion se aplica AL LOTE ENTERO, no fichero a fichero: quien
  // vuelca 200 facturas sabe que son 200 facturas, y clasificarlas una a una
  // no lo haria nadie. Nace sin clasificar y NO enviable.
  const [categoria, setCategoria] = useState('');
  const [enviable, setEnviable] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get('/users/list', { params: { role: 'company' } })
      .then(({ data }) => setCompanies(Array.isArray(data) ? data : []))
      .catch(() => setCompanies([]));
  }, [isAdmin]);

  if (!['admin', 'company'].includes(user?.role)) return <Navigate to='/dashboard' replace />;

  const companyParams = isAdmin ? { company_id: companyId } : {};
  const ready = !isAdmin || Boolean(companyId);

  const analyze = async fileList => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const tooBig = files.filter(f => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig.length) {
      toast.error(`Pasan de ${MAX_FILE_MB} MB: ${tooBig.map(f => f.name).join(', ')}`);
      return;
    }
    setBusy(true);
    setSummary(null);
    const next = [];
    try {
      for (const batch of chunks(files, BATCH_SIZE)) {
        const form = new FormData();
        batch.forEach(f => form.append('files', f));
        const { data } = await api.post('/bulk-documents/analyze', form, { params: companyParams });
        data.data.forEach((result, i) => {
          next.push({
            file: batch[i],
            result,
            // Se propone solo lo que el servidor ha encontrado sin dudas.
            choice: result.status === 'matched' ? result.candidates[0]?.id || '' : '',
            picked: null,
          });
        });
      }
      setRows(next);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const setChoice = (index, choice, picked = null) =>
    setRows(rs => rs.map((r, i) => (i === index ? { ...r, choice, picked: picked || r.picked } : r)));

  const toAttach = rows.filter(r => r.choice && !['duplicate', 'error'].includes(r.result.status));

  const attach = async () => {
    setBusy(true);
    const counts = { attached: 0, duplicate: 0, error: 0 };
    try {
      for (const batch of chunks(toAttach, BATCH_SIZE)) {
        const form = new FormData();
        batch.forEach(r => form.append('files', r.file));
        form.append('expedient_ids', JSON.stringify(batch.map(r => r.choice)));
        form.append(
          'classifications',
          JSON.stringify(batch.map(() => ({ category: categoria || null, shareable: enviable })))
        );
        const { data } = await api.post('/bulk-documents/attach', form, { params: companyParams });
        data.data.forEach(r => {
          counts[r.status] = (counts[r.status] || 0) + 1;
        });
      }
      setSummary(counts);
      setRows([]);
      // Se vuelve al estado seguro para el siguiente lote: que «enviable»
      // quedara marcado de la tanda anterior es justo como se le manda al
      // deudor algo que nadie quiso mandarle.
      setCategoria('');
      setEnviable(false);
      toast.success(`${counts.attached} documentos adjuntados`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <MainLayout>
      <div className='space-y-4'>
        <header className='bg-white border border-slate-200 rounded-lg px-5 py-4'>
          <div className='flex items-center gap-2'>
            <DocumentArrowUpIcon className='h-6 w-6 text-blue-600' />
            <h1 className='text-2xl font-semibold text-slate-900'>Volcado de documentos</h1>
          </div>
          <p className='text-sm text-slate-500 mt-1'>
            Sube muchos PDF a la vez y cada uno irá a su expediente. Se reconoce por el número de factura o el CIF del
            deudor, en el nombre del fichero o dentro del PDF. Revisa la propuesta antes de adjuntar.
          </p>
        </header>

        <div className='bg-white border border-slate-200 rounded-lg p-4 space-y-3'>
          {isAdmin && (
            <label className='flex flex-col text-sm text-slate-600 max-w-md'>
              Empresa
              <select
                value={companyId}
                onChange={e => {
                  setCompanyId(e.target.value);
                  setRows([]);
                  setSummary(null);
                }}
                className='mt-1 rounded-lg border border-slate-300 px-2 py-1.5'
              >
                <option value=''>Elige la empresa</option>
                {companies.map(c => (
                  <option key={c._id} value={c._id}>
                    {[c.username, c.userlastname].filter(Boolean).join(' ') || c.usermail}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className={`block text-sm ${ready ? 'text-slate-600' : 'text-slate-400'}`}>
            PDF (hasta {MAX_FILE_MB} MB cada uno)
            <input
              type='file'
              accept='application/pdf,.pdf'
              multiple
              disabled={!ready || busy}
              onChange={e => {
                analyze(e.target.files);
                e.target.value = '';
              }}
              className='mt-1 block text-sm'
              aria-label='Elegir PDF'
            />
          </label>
          <p className='text-xs text-slate-500'>
            Consejo: nombra cada PDF con su número de factura (por ejemplo, <span className='font-mono'>F-2026-0123.pdf</span>).
            Es lo más fiable, y sirve también para los escaneados, que no tienen texto que leer.
          </p>
          {busy && <p className='text-sm text-slate-500'>Procesando…</p>}
        </div>

        {summary && (
          <div className='bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800'>
            {summary.attached} adjuntados
            {summary.duplicate ? ` · ${summary.duplicate} ya estaban` : ''}
            {summary.error ? ` · ${summary.error} con error` : ''}.
          </div>
        )}

        {rows.length > 0 && (
          <div className='bg-white border border-slate-200 rounded-lg p-4 space-y-3'>
            <div className='flex flex-wrap items-center gap-4'>
              <label className='flex items-center gap-2 text-sm text-slate-600'>
                Clasificar los {toAttach.length} como
                <select
                  value={categoria}
                  onChange={e => setCategoria(e.target.value)}
                  disabled={busy}
                  className='rounded-lg border border-slate-300 px-2 py-1.5 text-sm'
                >
                  <option value=''>Sin clasificar</option>
                  {DOCUMENT_CATEGORIES.map(c => (
                    <option key={c} value={c}>
                      {DOCUMENT_CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </label>

              <label className='flex items-center gap-2 text-sm text-slate-600 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={enviable}
                  disabled={busy}
                  onChange={e => setEnviable(e.target.checked)}
                  className='rounded-lg border-slate-300'
                />
                Marcar los {toAttach.length} como enviables al deudor
              </label>
            </div>

            {enviable && (
              <p className='text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2'>
                Repasa las parejas antes de adjuntar: un documento en el expediente equivocado y
                marcado como enviable acaba en el buzón de otro deudor.
              </p>
            )}

            <p className='text-xs text-slate-400'>
              Solo se aplica a los expedientes de AR, que es donde se le mandan documentos al
              deudor.
            </p>
          </div>
        )}
        {rows.length > 0 && (
          <div className='bg-white border border-slate-200 rounded-lg p-4 space-y-3'>
            <div className='overflow-x-auto'>
              <table className='min-w-full text-sm'>
                <thead>
                  <tr className='text-left text-xs uppercase text-slate-500'>
                    <th className='py-2 pr-3'>Fichero</th>
                    <th className='py-2 pr-3'>Resultado</th>
                    <th className='py-2 pr-3 w-1/2'>Expediente</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  {rows.map((r, i) => {
                    const d = describeResult(r.result);
                    const options = [...(r.result.candidates || [])];
                    if (r.picked && !options.some(c => c.id === r.picked.id)) options.push(r.picked);
                    const blocked = ['duplicate', 'error'].includes(r.result.status);
                    return (
                      <tr key={`${r.file.name}-${i}`}>
                        <td className='py-2 pr-3 font-mono text-xs break-all'>{r.file.name}</td>
                        <td className={`py-2 pr-3 ${d.tone}`}>{d.text}</td>
                        <td className='py-2 pr-3 space-y-1'>
                          {blocked ? (
                            options[0] && <span className='text-slate-500'>{candidateLabel(options[0])}</span>
                          ) : (
                            <>
                              <select
                                value={r.choice}
                                onChange={e => setChoice(i, e.target.value)}
                                className='w-full rounded-lg border border-slate-300 px-2 py-1'
                                aria-label={`Expediente de ${r.file.name}`}
                              >
                                <option value=''>No adjuntar</option>
                                {options.map(c => (
                                  <option key={c.id} value={c.id}>
                                    {candidateLabel(c)}
                                  </option>
                                ))}
                              </select>
                              {r.result.status !== 'matched' && (
                                <ExpedientSearch companyId={isAdmin ? companyId : null} onPick={c => setChoice(i, c.id, c)} />
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className='flex items-center gap-3'>
              <button
                type='button'
                disabled={busy || toAttach.length === 0}
                onClick={attach}
                className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
              >
                Adjuntar {toAttach.length} {toAttach.length === 1 ? 'documento' : 'documentos'}
              </button>
              <span className='text-xs text-slate-500'>Los que estén en «No adjuntar» no se suben.</span>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default BulkDocumentsPage;
